// frontend/src/ts/protocols/persistence/uploadCoordinator.ts
//
// Thin shim layer that delegates to the active UploadStore.
//
// `uploadFileInBrowser` is the browser-mode direct upload path (XHR
// PUT to S3 with onUploadProgress driven by BrowserUploadStore).
//
// Tauri mode does NOT call this function. Tauri drag/drop and picker
// both flow through enqueuePickedFile(s) / pickFile -> enqueue_picked_file
// on the active store, which inserts a 'pending' row in pending_uploads.
// The Rust upload worker then streams the local copy to S3 and the
// file list reflects that progress via polling pendingUploads.

import { getUploadStore } from './uploadStore'
import { useUploadStatusStore } from './useUploadStatusStore'
import { useNeurotuneStore } from '@/common/store/neurotune'

export interface UploadFileInBrowserArgs {
  companyId: string
  protocolId: string
  file: File
  subjectId: string
  protocolStepId?: string
  onProgress?: (bytesWritten: number, totalBytes: number) => void
  /** Cancels the underlying presign/PUT requests when aborted. */
  signal?: AbortSignal
}

// Browser uploads are durable: the entry retains its File + context, so
// every retry re-runs the whole upload (new presigned URL + PUT from
// byte 0) without user interaction. Attempts are therefore NEVER capped
// (manual or automatic) — a persisted file must eventually land. Storm
// protection comes from the trigger shape instead: auto-retry fires at
// most once per offline→online transition, not per failure.

/** True for user-initiated cancels: DOM AbortError or axios's
 * CanceledError (code ERR_CANCELED) when the AbortController signal
 * fires mid-request. */
export function isUploadAbortError(e: unknown): boolean {
  if (e instanceof DOMException && e.name === 'AbortError') return true
  return (
    typeof e === 'object' && e !== null &&
    'code' in e && (e as { code?: string }).code === 'ERR_CANCELED'
  )
}

/**
 * Browser-mode file upload flow. The XHR's own onUploadProgress
 * (forwarded by `BrowserUploadStore.finalizeBrowserUpload` via the
 * `onProgress` callback) drives the browserUploads slice entry's
 * progress in `useUploadStatusStore`. There is no chunk loop.
 *
 * Tauri mode does NOT call this function — see file header.
 *
 * The streaming IPC surface (start/finalize/abort) lives only on
 * `BrowserUploadShape`, which the active `BrowserUploadStore`
 * exposes via `store.browserUpload`. TauriUploadStore does not have
 * one (Tauri uploads go through `enqueuePickedFiles` / `pickFile`
 * instead).
 */
export async function uploadFileInBrowser(args: UploadFileInBrowserArgs): Promise<number> {
  const { companyId, protocolId, file, subjectId, protocolStepId, onProgress, signal } = args
  const store = getUploadStore()
  const browser = store.browserUpload
  if (!browser) {
    throw new Error('uploadFileInBrowser: store has no browser upload surface (Tauri mode?)')
  }

  const { uploadId } = await browser.startBrowserUpload({
    companyId, protocolId, filename: file.name,
    file,
    subjectId,
    protocolStepId,
  })

  try {
    return await browser.finalizeBrowserUpload({
      companyId, protocolId, uploadId, filename: file.name, fileSize: file.size,
      file,
      subjectId,
      protocolStepId,
      onProgress,
      signal,
    })
  } catch (e) {
    const error = e instanceof Error ? e.message : String(e)
    try {
      await browser.abortBrowserUpload({
        companyId, protocolId, uploadId, filename: file.name, fileSize: file.size, error,
      })
    } catch (e2) {
      console.error('[uploadFileInBrowser] abortBrowserUpload also failed', e2)
    }
    console.error('[uploadFileInBrowser] upload failed', e)
    throw e
  }
}

/**
 * Run (or re-run) the browser upload flow for one browserUploads entry,
 * reusing its tempId so the row transitions dead → in_flight → (done |
 * dead) in place instead of vanishing. The retained File + context make
 * this possible without asking the user to re-pick the file.
 */
async function runBrowserUpload(tempId: number): Promise<void> {
  const entry = useUploadStatusStore.getState().browserUploads.get(tempId)
  if (!entry || !entry.file || !entry.context) return

  const abort = new AbortController()
  useUploadStatusStore.getState().restartBrowserUpload(tempId, abort)
  try {
    await uploadFileInBrowser({
      companyId: entry.context.companyId,
      protocolId: entry.context.protocolId,
      file: entry.file,
      subjectId: entry.context.subjectId,
      protocolStepId: entry.context.protocolStepId,
      signal: abort.signal,
      onProgress: (bytesWritten, totalBytes) => {
        useUploadStatusStore.getState().updateBrowserUploadProgress(
          tempId, bytesWritten, totalBytes > 0 ? bytesWritten / totalBytes : 0,
        )
      },
    })
    useUploadStatusStore.getState().completeBrowserUpload(tempId)
  } catch (e) {
    if (isUploadAbortError(e)) {
      useUploadStatusStore.getState().cancelBrowserUpload(tempId)
    } else {
      const err = e instanceof Error ? e.message : String(e)
      useUploadStatusStore.getState().failBrowserUpload(tempId, err)
      console.error('[uploadCoordinator] browser upload retry failed', e)
      const { showErrorNotification } = await import('@/common/notifications')
      showErrorNotification({
        title: 'Upload failed',
        message: `Could not upload ${entry.filename}: ${err}`,
      })
    }
  }
}

/**
 * Retry one dead browser upload. No-op unless the entry is dead and
 * still retains its File. Unlimited: browser uploads are durable (see
 * file-top note), so both manual Retry and auto-retry-on-reconnect may
 * attempt them indefinitely.
 */
export async function retryBrowserUpload(tempId: number): Promise<void> {
  const entry = useUploadStatusStore.getState().browserUploads.get(tempId)
  if (!entry || entry.status !== 'dead') return
  await runBrowserUpload(tempId)
}

/** Retry every dead browser upload that still has its File. One attempt
 * per entry per call; callers gate when that happens (see
 * startReconnectAutoRetry). */
export function retryAllDeadBrowserUploads(): void {
  const { browserUploads } = useUploadStatusStore.getState()
  browserUploads.forEach((entry) => {
    if (entry.status === 'dead') void retryBrowserUpload(entry.tempId)
  })
}

/**
 * Watch the app-level connectivity state and auto-retry dead browser
 * uploads whenever it flips offline → online. Returns an unsubscribe
 * function for effect cleanup.
 *
 * Why the store state and not the window `online` event: `navigator.onLine`
 * only tracks the OS network interface. WiFi associated with the internet
 * down (the common real-world outage) never fires `online`/`offline`, and
 * WKWebView's events are unreliable — while the whoAmI probe that drives
 * `isOnline` is authoritative in both browsers and NDC. The probe effect
 * also runs an immediate probe on the window `online` event, so genuine
 * interface recoveries still retry within one probe round-trip.
 */
export function startReconnectAutoRetry(): () => void {
  return useNeurotuneStore.subscribe((state, prev) => {
    if (!prev.isOnline && state.isOnline) retryAllDeadBrowserUploads()
  })
}
